import { create } from 'zustand'

// Gmail-style "Send with Undo" — scheduling lives at the app level
// (not inside MailComposer) so the 10s undo window survives:
//   - closing the composer immediately on Send click
//   - navigating between threads / folders
//   - switching the active mailbox
// The composer itself never blocks on the delay; it fires the
// snapshot here and unmounts. The toast subscribes to this store
// and renders the "Сообщение отправлено · Отменить" bar; clicking
// Undo restores the snapshot into the composer.

export interface PendingSendSnapshot {
  /** MailDraft cuid if the autosave ever ran; null on hot-send. */
  draftId: string | null
  accountId: string | number
  to: string[]
  cc?: string[]
  bcc?: string[]
  subject: string
  bodyText: string
  bodyHtml?: string
  inReplyToMessageId?: string
  threadId?: string
  // OSPROD-163: attachment refs queued in the composer, carried through the
  // undo window so the actual send embeds them.
  attachments?: Array<{ fileName: string; fileUrl: string; fileSize?: number; mimeType: string }>
}

export type PendingSendFireFn = (s: PendingSendSnapshot) => Promise<void>

interface PendingState {
  pending: {
    snapshot: PendingSendSnapshot
    scheduledAt: number  // ms timestamp when the actual send fires
    delayMs: number
  } | null
  /**
   * Schedule a send `delayMs` from now. Cancels any in-flight
   * pending send first (we only show one toast at a time). Returns
   * the scheduledAt timestamp so the caller can drive its own
   * countdown if needed.
   */
  schedule: (snapshot: PendingSendSnapshot, delayMs: number, fire: PendingSendFireFn) => number
  /**
   * Cancel the pending send WITHOUT firing it. Returns the snapshot
   * so the caller (the toast's Undo button) can reopen the composer
   * pre-loaded with the unsent content.
   */
  cancel: () => PendingSendSnapshot | null
  /**
   * Fire RIGHT NOW (skip the remaining countdown). The toast's
   * close (×) button uses this — Gmail's pattern is "if you dismiss
   * the toast, you committed to sending".
   */
  fireNow: () => void
}

// Module-level timer ref so the timer survives any component
// unmount (React Strict Mode double-mount in dev, navigation
// during the undo window in prod).
let activeTimer: ReturnType<typeof setTimeout> | null = null
let activeFire: PendingSendFireFn | null = null
let activeSnapshot: PendingSendSnapshot | null = null

function clearTimer(): void {
  if (activeTimer) clearTimeout(activeTimer)
  activeTimer = null
  activeFire = null
  activeSnapshot = null
}

export const usePendingMailSendStore = create<PendingState>((set, get) => ({
  pending: null,
  schedule(snapshot, delayMs, fire) {
    clearTimer()
    const scheduledAt = Date.now() + delayMs
    activeSnapshot = snapshot
    activeFire = fire
    activeTimer = setTimeout(async () => {
      const s = activeSnapshot
      const f = activeFire
      clearTimer()
      set({ pending: null })
      if (s && f) {
        try { await f(s) } catch (err) {
          console.warn('[pending-mail-send] fire failed', err)
        }
      }
    }, delayMs)
    set({ pending: { snapshot, scheduledAt, delayMs } })
    return scheduledAt
  },
  cancel() {
    const snap = activeSnapshot
    clearTimer()
    set({ pending: null })
    return snap
  },
  fireNow() {
    const s = activeSnapshot
    const f = activeFire
    clearTimer()
    set({ pending: null })
    if (s && f) {
      void f(s).catch((err) => console.warn('[pending-mail-send] fire failed', err))
    }
  },
}))

/**
 * Convenience selector that returns the live remaining-seconds
 * value. Components subscribing here re-render at the cadence they
 * sample at; the toast drives a 250ms tick of its own.
 */
export function remainingSeconds(): number {
  const p = usePendingMailSendStore.getState().pending
  if (!p) return 0
  return Math.max(0, Math.ceil((p.scheduledAt - Date.now()) / 1000))
}
